import { HttpContext } from '@angular/common/http';
import { escapeDelimiters, QueryHeaders } from '../query-params.model';

type Primitive = string | number;

type FilterValue = Primitive | Primitive[];

// Filter logical operators
export type FilterLogicalOperator = ';' | ',';

// Filter comparison operators
export type FilterComparisonOperator = '==' | '!=' | '=@' | '!@' | '>' | '<' | '>=' | '<=';

type FieldComparatorMap = Record<string, FilterComparisonOperator | FilterComparisonOperator[]>;

type AllowedOperators<T> = T extends FilterComparisonOperator
  ? T
  : T extends (infer U)[]
    ? U
    : never;

type FieldAllows<T extends FieldComparatorMap, Op extends FilterComparisonOperator> = {
  [K in keyof T]: Op extends AllowedOperators<T[K]> ? K : never;
}[keyof T];

// Sorting fields
type SortingDirection = 'asc' | 'desc';
type SortingDirectionConfig =
  | 'asc'
  | 'desc'
  | Array<'asc' | 'desc'>
  | ReadonlyArray<'asc' | 'desc'>;
// Infer allowed direction for a given field
type AllowedSortDirection<T, K extends keyof T> = T[K] extends SortingDirection
  ? T[K]
  : T[K] extends (infer U)[]
    ? U
    : never;

// Ensure K is one of the allowed sortable fields
type SortableFieldAllowed<T> = {
  [K in keyof T]: T[K] extends SortingDirectionConfig ? K : never;
}[keyof T];

export type ListRequestConfig = RequestConfig & {
  query?: ListRequestQuery;
};

export type ListRequestQuery = {
  filter?: string;
  sort?: string;
  limit?: number;
  offset?: number;
};

export interface RequestConfig {
  headers?: QueryHeaders;
  context?: HttpContext;
  withAccountId?: string;
  query?: {
    filter?: string;
    sort?: string;
    limit?: number;
    offset?: number;
  };
}

export class FluentQueryBuilder<
  T extends FieldComparatorMap,
  SortFields extends Partial<Record<keyof T, SortingDirectionConfig>>,
> {
  private filter = '';
  private operator: FilterLogicalOperator = ';';
  private sorting = '';
  private sortOperator: FilterLogicalOperator = ';';

  /**
   * Sets the logical operator for combining filter conditions to AND (`;`).
   * This means subsequent filter conditions will be joined using the AND operator.
   *
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   */
  public setFilterAnd(): this {
    this.operator = ';';
    return this;
  }

  /**
   * Sets the logical operator for combining filter conditions to OR (`,`).
   * The default operator is `;` (AND).
   * This means ONLY ONE subsequent filter condition will be joined using the OR operator.
   * After that, the operator will be reset to `;` (AND).
   *
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   */
  public setFilterOr(): this {
    this.operator = ',';
    return this;
  }

  /**
   * Sets the logical operator for combining sort conditions to OR (`,`).
   * This means ONLY ONE subsequent sort conditions will be joined using the AND operator.
   * After that, the operator will be reset to `;` (AND).
   *
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   */
  public setSortOr(): this {
    this.sortOperator = ',';
    return this;
  }

  /**
   * Sets the logical operator for combining sort conditions to AND (`;`).
   * This means subsequent sort conditions will be joined using the AND operator.
   *
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   */
  public setSortAnd(): this {
    this.sortOperator = ';';
    return this;
  }

  /**
   * Adds a filter equal (==) condition.
   *
   * Provide an available filter field name and a value to compare against.
   *
   * Values can be provided as single and an arrays of values. If an array is provided,
   * the conditions will be joined using the OR operator (`,`).
   *
   * The default operator when combining different fields is `;` (AND).
   *
   * @template K - The field name that supports the `==` operator.
   * @param {K extends FieldAllows<T, '=='>} field - The name of the field to filter on.
   * @param {FilterValue} value - The value or array of values to compare against.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .equal('status', 'active')
   *  .build();
   *
   *  const query2 = builder
   *  .equal('status', ['active', 'pending'])
   *  .build();
   *
   */
  public equal<K extends FieldAllows<T, '=='>>(field: K, value: FilterValue): this {
    return this.addCondition(String(field), '==', value);
  }

  /**
   * Adds a filter not equal (!=) condition.
   *
   * Provide an available filter field name and a value to compare against.
   *
   * Values can be provided as single or as an arrays of values. If an array is provided,
   * the conditions will be joined using the OR operator (`,`).
   *
   * The default operator when combining different fields is `;` (AND).
   *
   * @template K - The field name that supports the `!=` operator.
   * @param field - The name of the field to filter on.
   * @param value - The value or array of values to compare against.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .notEqual('status', 'active')
   *  .build();
   *
   *  const query2 = builder
   *  .notEqual('status', ['active', 'inactive'])
   *  .build();
   *
   */
  public notEqual<K extends FieldAllows<T, '!='>>(field: K, value: FilterValue): this {
    return this.addCondition(String(field), '!=', value);
  }

  /**
   * Adds a contains (=@) filter condition.
   *
   * Provide an available filter field name and a value to compare against.
   *
   * Values can be provided as single or as an arrays of values. If an array is provided,
   * the conditions will be joined using the OR operator (`,`).
   *
   * The default operator when combining different fields is `;` (AND).
   *
   * @template K - The field name that supports the `=@` operator.
   * @param field - The name of the field to filter on.
   * @param value - The value or array of values to compare against.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .contains('name', 'Mickey')
   *  .build();
   *
   *  const query2 = builder
   *  .contains('name', ['Mickey', 'Mouse'])
   *  .build();
   *
   */
  public contains<K extends FieldAllows<T, '=@'>>(field: K, value: FilterValue): this {
    return this.addCondition(String(field), '=@', value);
  }

  /**
   * Adds a not contains (!@) filter condition.
   *
   * Provide an available filter field name and a value to compare against.
   *
   * Values can be provided as single or as an arrays of values. If an array is provided,
   * the conditions will be joined using the OR operator (`,`).
   *
   * The default operator when combining different fields is `;` (AND).
   *
   * @template K - The field name that supports the `!@` operator.
   * @param field - The name of the field to filter on.
   * @param value - The value or array of values to compare against.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .notContains('name', 'Mickey')
   *  .build();
   *
   *  const query2 = builder
   *  .notContains('name', ['Mickey', 'Mouse'])
   *  .build();
   *
   */
  public notContains<K extends FieldAllows<T, '!@'>>(field: K, value: FilterValue): this {
    return this.addCondition(String(field), '!@', value);
  }

  /**
   * Adds greater then (>) filter condition.
   *
   * Provide an available filter field name and a value to compare against.
   *
   * Values can be provided as single or as an arrays of values. If an array is provided,
   * the conditions will be joined using the OR operator (`,`).
   *
   * The default operator when combining different fields is `;` (AND).
   *
   * @template K - The field name that supports the `>` operator.
   * @param field - The name of the field to filter on.
   * @param value - The value or array of values to compare against.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .greaterThan('quantity', 10)
   *  .build();
   *
   *  const query2 = builder
   *  .greaterThan('quantity', [10, 1000])
   *  .build();
   *
   */
  public greaterThan<K extends FieldAllows<T, '>'>>(field: K, value: FilterValue): this {
    return this.addCondition(String(field), '>', value);
  }

  /**
   * Adds less then (<) filter condition.
   *
   * Provide an available filter field name and a value to compare against.
   *
   * Values can be provided as single or as an arrays of values. If an array is provided,
   * the conditions will be joined using the OR operator (`,`).
   *
   * The default operator when combining different fields is `;` (AND).
   *
   * @template K - The field name that supports the `<` operator.
   * @param field - The name of the field to filter on.
   * @param value - The value or array of values to compare against.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .lessThan('quantity', 10)
   *  .build();
   *
   *  const query2 = builder
   *  .lessThan('quantity', [10, 1000])
   *  .build();
   *
   */
  public lessThan<K extends FieldAllows<T, '<'>>(field: K, value: FilterValue): this {
    return this.addCondition(String(field), '<', value);
  }

  /**
   * Adds greater then or equal (>=) filter condition.
   *
   * Provide an available filter field name and a value to compare against.
   *
   * Values can be provided as single or as an arrays of values. If an array is provided,
   * the conditions will be joined using the OR operator (`,`).
   *
   * The default operator when combining different fields is `;` (AND).
   *
   * @template K - The field name that supports the `>=` operator.
   * @param field - The name of the field to filter on.
   * @param value - The value or array of values to compare against.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .greaterThanOrEqual('quantity', 10)
   *  .build();
   *
   *  const query2 = builder
   *  .greaterThanOrEqual('quantity', [10, 1000])
   *  .build();
   *
   */
  public greaterThanOrEqual<K extends FieldAllows<T, '>='>>(field: K, value: FilterValue): this {
    return this.addCondition(String(field), '>=', value);
  }

  /**
   * Adds less then or equal (<=) filter condition.
   *
   * Provide an available filter field name and a value to compare against.
   *
   * Values can be provided as single or as an arrays of values. If an array is provided,
   * the conditions will be joined using the OR operator (`,`).
   *
   * The default operator when combining different fields is `;` (AND).
   *
   * @template K - The field name that supports the `<=` operator.
   * @param field - The name of the field to filter on.
   * @param value - The value or array of values to compare against.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .lessThanOrEqual('quantity', 10)
   *  .build();
   *
   *  const query2 = builder
   *  .lessThanOrEqual('quantity', [10, 1000])
   *  .build();
   *
   */
  public lessThanOrEqual<K extends FieldAllows<T, '<='>>(field: K, value: FilterValue): this {
    return this.addCondition(String(field), '<=', value);
  }

  /**
   * Adds a sorting condition to the query.
   *
   * Provide a sortable field name and the available sorting direction (asc or desc).
   *
   * The default operator when combining different sort fields is `;` (AND).
   * If `setSortOr()` is called before this method, the sort conditions will be joined using the OR operator (`,`).
   * After adding the condition, the operator will reset to `;` (AND).
   *
   * @template K - The field name that supports sorting.
   * @param field - The name of the field to sort on.
   * @param direction - The sorting direction (asc or desc) for the specified field.
   * @returns The current instance of `FluentQueryBuilder` for method chaining.
   *
   * @example
   *
   * const query = builder
   *  .sort('name', 'asc')
   *  .sort('createdAt', 'desc')
   *  .build();
   *
   *  const query2 = builder
   *  .sort('name', 'asc')
   *  .setSortOr()
   *  .sort('createdAt', 'desc')
   *  .build();
   */
  public sort<K extends SortableFieldAllowed<SortFields>>(
    field: K,
    direction: AllowedSortDirection<SortFields, K>
  ): this {
    const condition = `${String(field)}=${direction}`;
    this.sorting = this.sorting ? `${this.sorting}${this.sortOperator}${condition}` : condition;
    this.sortOperator = ';';
    return this;
  }

  /**
   * Builds the query configuration object.
   *
   * @param options - Optional configuration for the request.
   * @param options.limit - The maximum number of items to return.
   * @param options.offset - The offset for pagination.
   * @param options.headers - Additional headers to include in the request.
   * @returns The constructed query configuration object.
   */
  public build(
    options: Pick<ListRequestConfig, 'headers' | 'withAccountId' | 'context'> & {
      limit?: number;
      offset?: number;
    } = {}
  ): ListRequestConfig {
    const { limit, offset, headers, context, withAccountId } = options;
    const { filter } = this.buildFilter();
    const { sort } = this.buildSort();
    const filteredQuery = Object.entries({ filter, sort, limit, offset }).reduce(
      (acc, [key, value]) => (value !== undefined ? { ...acc, [key]: value } : acc),
      {}
    );

    return {
      query: {
        ...filteredQuery,
      },
      ...(headers && { headers }),
      ...(context && { context }),
      ...(withAccountId && { withAccountId }),
    };
  }

  private addCondition(
    field: string,
    operator: FilterComparisonOperator,
    value: FilterValue
  ): this {
    const values = Array.isArray(value) ? value : [value];
    const conditions = values.map((v) => `${field}${operator}${this.escapeValue(v)}`).join(',');
    this.filter = this.filter ? `${this.filter}${this.operator}${conditions}` : conditions;
    this.operator = ';';
    return this;
  }

  private escapeValue(value: Primitive): Primitive {
    return typeof value === 'string' ? escapeDelimiters(value) : value;
  }

  private buildFilter(): { filter?: string } {
    return {
      ...(this.filter.length > 0 && { filter: this.filter }),
    };
  }

  private buildSort(): { sort?: string } {
    return {
      ...(this.sorting.length > 0 && { sort: this.sorting }),
    };
  }
}

/**
 * Creates a new instance of the `FluentQueryBuilder` class.
 *
 * @template T - The type of filter fields and their allowed operators.
 * @template S - The type of sortable fields and their allowed sorting directions.
 * @returns A new instance of the `FluentQueryBuilder` class.
 */
export function createQueryBuilder<
  T extends FieldComparatorMap,
  S extends Partial<Record<keyof T, SortingDirectionConfig>>,
>() {
  return new FluentQueryBuilder<T, S>();
}
